/**
 * DamageChecklist — fahrzeugspezifische Schadens-Checkliste im Vorgangsformular.
 * Identisch zur Kunden-Landingpage: Positionen werden je Fahrzeug (Antrieb/ccm)
 * gefiltert. Vom Kunden markierte Positionen sind über `answers` vorausgewählt.
 * Die berechnete Brutto-Summe wird via onChange(answers, estimate) nach oben
 * gereicht und fließt in die Rückgabe-/Schadenkosten ein.
 */
import { useEffect, useRef, useState } from "react";
import { Wrench, ChevronDown, Loader2, Info } from "lucide-react";
import api from "../../lib/api";

const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function DamageChecklist({ vehicle, answers: initial, onChange, context = "leasing", startOpen = true }) {
    const [answers, setAnswers] = useState(initial || {});
    const [open, setOpen] = useState(startOpen || Object.keys(initial || {}).length > 0);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);
    const isLeasing = context === "leasing";

    const compute = (ans) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setBusy(true);
            try {
                const { data: res } = await api.post("/leasing-returns/damage-catalog/compute", { vehicle, answers: ans, context });
                setData(res);
                onChange?.(ans, res.estimate);
            } catch { /* Katalog evtl. leer */ } finally { setBusy(false); }
        }, 350);
    };

    const vehKey = JSON.stringify([vehicle.manufacturer, vehicle.model, vehicle.fuel, vehicle.displacement, vehicle.vin, vehicle.year, vehicle.mileage, vehicle.first_registration_date]);
    // Bei Fahrzeugänderung neu berechnen (Katalog hängt von Antrieb/ccm ab).
    useEffect(() => { compute(answers); return () => timer.current && clearTimeout(timer.current); /* eslint-disable-next-line */ }, [vehKey]);

    const set = (k, v) => { const ans = { ...answers, [k]: v }; setAnswers(ans); compute(ans); };

    const estimate = data?.estimate;
    const total = estimate?.total || 0;
    const groups = data?.groups || [];

    return (
        <div className="mt-4 border-2 border-gray-200 rounded-sm bg-gray-50/60 overflow-hidden" data-testid="leasing-damage-checklist">
            <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-100 transition-colors" aria-expanded={open} data-testid="leasing-damage-checklist-toggle">
                <Wrench className="w-4 h-4 text-[#111827]" />
                <span className="font-bold text-sm uppercase tracking-wider text-[#111827] flex-1">{isLeasing ? "Schadensermittlung (lt. Rückgabe-Checkliste)" : "Schadensermittlung — Technik / Optik"}</span>
                {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                {total > 0 && <span className="text-[11px] font-bold text-red-600" data-testid="leasing-damage-checklist-total-badge">{eur(total)}</span>}
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-3" data-testid="leasing-damage-checklist-content">
                    <div className="text-[11px] text-gray-500 flex items-start gap-1.5">
                        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {data?.disclaimer || "Fahrzeugspezifische Rückgabe-Positionen. Vom Kunden markierte Positionen sind vorausgewählt."}
                    </div>

                    {groups.length === 0 ? (
                        <p className="text-[11px] text-gray-400" data-testid="leasing-damage-checklist-empty">
                            {busy ? "Katalog wird geladen…" : "Kein Katalog verfügbar — bitte Fahrzeugdaten (Antrieb/Hubraum) ergänzen oder Katalog in „Freigabe & Texte“ pflegen."}
                        </p>
                    ) : groups.map((g) => (
                        <div key={g.group} className="bg-white border rounded-sm p-3" data-testid={`leasing-damage-group-${g.group}`}>
                            <div className="font-bold text-xs uppercase tracking-wider text-gray-700 mb-2">{g.group}</div>
                            <div className="space-y-1.5">
                                {g.items.map((it) => (
                                    <div key={it.key} className="flex flex-wrap items-center gap-2 py-1 border-b border-gray-50 last:border-0 text-sm">
                                        {it.input === "auto" && (
                                            <div className="flex-1 flex justify-between text-gray-600"><span>{it.label}{it.key === "service" && <em className="text-gray-400 not-italic"> · fällt immer an</em>}</span><span className="font-semibold">{eur(it.price)}</span></div>
                                        )}
                                        {it.input === "bool" && (
                                            <label className="flex-1 flex items-center gap-2 cursor-pointer" data-testid={`leasing-damage-${it.key}`}>
                                                <input type="checkbox" checked={!!answers[it.key]} onChange={(e) => set(it.key, e.target.checked)} />
                                                <span className="flex-1">{it.label}</span>
                                                <span className="text-gray-400">{eur(it.price)}</span>
                                            </label>
                                        )}
                                        {it.input === "manual" && (
                                            <div className="flex-1 flex items-center gap-2">
                                                <span className="flex-1">{it.label}</span>
                                                <input type="number" min="0" placeholder="€" value={answers[it.key] || ""} onChange={(e) => set(it.key, e.target.value)} className="w-24 border rounded-sm px-2 py-1" data-testid={`leasing-damage-${it.key}`} />
                                            </div>
                                        )}
                                        {it.input === "select" && (
                                            <div className="flex-1 flex items-center gap-2">
                                                <span className="flex-1">{it.label}</span>
                                                <select value={answers[it.key] || "none"} onChange={(e) => set(it.key, e.target.value)} className="border rounded-sm px-2 py-1" data-testid={`leasing-damage-${it.key}`}>
                                                    {it.options.map((o) => <option key={o.v} value={o.v}>{o.label}{o.price ? ` (${eur(o.price)})` : ""}</option>)}
                                                </select>
                                            </div>
                                        )}
                                        {it.input === "maintenance" && (
                                            <div className="flex-1 space-y-2" data-testid="leasing-damage-maintenance">
                                                <div className="text-gray-600">{it.label}</div>
                                                <div className="flex flex-wrap gap-3 items-center">
                                                    <label className="flex items-center gap-1">Durchgeführte Wartungen:
                                                        <input type="number" min="0" value={answers.maintenance_done || ""} onChange={(e) => set("maintenance_done", e.target.value)} className="w-16 border rounded-sm px-2 py-1" data-testid="leasing-damage-maint-done" />
                                                    </label>
                                                    <label className="flex items-center gap-1">km-Stand:
                                                        <input type="number" min="0" value={answers.maintenance_km || ""} onChange={(e) => set("maintenance_km", e.target.value)} className="w-24 border rounded-sm px-2 py-1" data-testid="leasing-damage-maint-km" />
                                                    </label>
                                                    <label className="flex items-center gap-1 cursor-pointer">
                                                        <input type="checkbox" checked={!!answers.maintenance_kws} onChange={(e) => set("maintenance_kws", e.target.checked)} data-testid="leasing-damage-maint-kws" /> KWS vorhanden
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {estimate && (estimate.lines || []).length > 0 && (
                        <div className="bg-white border-2 border-indigo-200 rounded-sm p-3" data-testid="leasing-damage-checklist-result">
                            <div className="space-y-1 text-sm">
                                {estimate.lines.map((l, i) => (
                                    <div key={i} className="flex justify-between border-b border-gray-50 py-1"><span className="text-gray-700">{l.label}</span><span className="font-semibold">{eur(l.amount)}</span></div>
                                ))}
                            </div>
                            <div className="flex justify-between mt-2 pt-2 border-t-2 font-black" data-testid="leasing-damage-checklist-total">
                                <span>Summe (Brutto, inkl. 19 % MwSt.)</span><span className="text-red-600">{eur(total)}</span>
                            </div>
                            {isLeasing && <p className="text-[10px] text-gray-400 mt-1">Diese Summe wird automatisch als Rückgabe-/Schadenkosten übernommen.</p>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
